import { css, cx } from "@emotion/css";
import { resetStyles, Tooltip } from "@octopusdeploy/design-system-components";
import { ChannelIcon, EnvironmentIcon, MessageLinesIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, colorScales, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ChannelResource } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import type { ReactNode } from "react";
import type React from "react";
import { RunInParallelIndicator } from "~/areas/projects/components/Process/RunInParallelIndicator";
import InternalLink from "~/components/Navigation/InternalLink";
export interface ProcessOverviewStepBaseData {
    id: string;
    name: string;
    href: LinkHref;
    logo: {
        src: string;
    } | React.ReactElement;
    logoTooltip: string;
    isDisabled: boolean;
    summary: ReactNode;
    isRunInParallelWithPreviousStep: boolean;
}
export interface ProcessOverviewStepActionData extends ProcessOverviewStepBaseData {
    environmentConfig: StepEnvironmentConfig;
    channelConfig?: StepChannelConfig;
    note?: ReactNode;
}
export type StepEnvironmentConfig = {
    type: "selected" | "skipped";
    environmentNames: string[];
} | {
    type: "all";
};
export interface ChannelsLookup {
    Key: string;
    Channel: ChannelResource;
}
export interface StepChannelConfig {
    channelsLookup?: ChannelsLookup[];
    channelsVariable?: string | null;
}
export interface ProcessOverviewStepActionProps {
    stepActionData: ProcessOverviewStepActionData;
    showNote: boolean;
    isRootStep: boolean;
}
export function ProcessOverviewStepAction({ stepActionData, showNote, isRootStep }: ProcessOverviewStepActionProps) {
    const { name, logo, logoTooltip, href, note, isDisabled, summary, environmentConfig, channelConfig } = stepActionData;
    return (<InternalLink className={cx(styles.root, { [styles.rootStep]: isRootStep })} to={href}>
            {stepActionData.isRunInParallelWithPreviousStep && <RunInParallelIndicator />}
            <Tooltip className={styles.iconContainer} content={logoTooltip}>
                {"src" in logo ? <img className={styles.icon} src={logo.src} alt=""/> : logo}
            </Tooltip>
            <div className={cx(styles.stepContent, { [styles.disabled]: isDisabled })}>
                <div className={styles.stepContentTop}>
                    <div className={styles.title}>
                        <span className={cx({ [styles.nameDisabled]: isDisabled })}>{name}</span>
                        {!showNote && note && <MessageLinesIcon size={20} color={themeTokens.color.icon.tertiary} ariaLabel={"Step note available"}/>}
                    </div>
                    {summary}
                    <div className={styles.configSection}>
                        {showEnvironmentDescription(environmentConfig) && (<p className={styles.environmentConfig}>
                                <span className={styles.environmentIconWrapper}>
                                    <EnvironmentIcon size={16} color={themeTokens.color.icon.secondary}/>
                                </span>
                                <span className={styles.environmentDescription}>{getEnvironmentDescription(environmentConfig)}</span>
                            </p>)}
                        {channelConfig && hasChannelConfig(channelConfig) && (<p className={styles.channelConfig}>
                                <span className={styles.channelIconWrapper}>
                                    <ChannelIcon size={16} color={themeTokens.color.icon.secondary}/>
                                </span>
                                <span className={styles.channelDescription}>{getChannelDescription(channelConfig)}</span>
                            </p>)}
                    </div>
                </div>
                {showNote && note && <div className={styles.stepNote}>{note}</div>}
            </div>
        </InternalLink>);
}
function showEnvironmentDescription(environmentConfig: StepEnvironmentConfig) {
    if (environmentConfig.type === "all")
        return true;
    return environmentConfig.environmentNames?.length > 0;
}
function getEnvironmentDescription(environmentConfig: StepEnvironmentConfig) {
    switch (environmentConfig.type) {
        case "all":
            return "Runs for any environment";
        case "selected":
            return `Runs for: ${environmentConfig.environmentNames.join(", ")}`;
        case "skipped":
            return `Skips: ${environmentConfig.environmentNames.join(", ")}`;
        default:
            exhaustiveCheck(environmentConfig, "Not all types of step environment config have been handled");
    }
}
function hasChannelConfig(channelConfig: StepChannelConfig): boolean {
    return Boolean(channelConfig.channelsLookup?.length || channelConfig.channelsVariable);
}
function getChannelDescription(channelConfig: StepChannelConfig): string {
    const descriptions: string[] = [];
    if (channelConfig.channelsLookup?.length) {
        const channelNames = channelConfig.channelsLookup.map((lookup) => lookup.Channel.Name);
        descriptions.push(`Applies to: ${channelNames}`);
    }
    if (channelConfig.channelsVariable) {
        descriptions.push(`${channelConfig.channelsVariable} (parameter)`);
    }
    return descriptions.join(", ");
}
const stepNoteBackgroundCssVariable = "--step-note-background";
const styles = {
    root: css({
        ...resetStyles.anchor,
        position: "relative",
        display: "flex",
        flex: 1,
        padding: `${space[12]}`,
        gap: space[12],
        border: `${borderWidth[1]} solid ${themeTokens.color.processStep.border.default}`,
        backgroundColor: themeTokens.color.processStep.background.primary.default,
        borderRadius: borderRadius.medium,
        font: text.body.regular.small,
        color: themeTokens.color.text.primary,
        transitionProperty: "background-color, border-color",
        transitionDuration: "0.1s",
        ":focus-visible": {
            boxShadow: themeTokens.shadow.focused,
        },
        ":hover": {
            color: themeTokens.color.text.primary, // prevent global link hover styles applying
            borderColor: themeTokens.color.processStep.border.hover,
            backgroundColor: themeTokens.color.processStep.background.hover.primary,
            [stepNoteBackgroundCssVariable]: `rgb(from ${themeTokens.color.processStep.background.hover.secondary} r g b / 0.5)`,
        },
    }),
    stepContent: css({
        display: "flex",
        flex: 1,
        flexDirection: "column",
        gap: space[8],
    }),
    stepContentTop: css({
        display: "flex",
        gap: space[2],
        flexDirection: "column",
    }),
    iconContainer: css({
        background: colorScales.white,
        borderRadius: borderRadius.extraSmall,
        alignContent: "center",
        height: "2.5rem",
        width: "2.5rem",
        overflow: "clip",
    }),
    icon: css({
        display: "block",
        objectFit: "scale-down",
        width: "100%",
    }),
    title: css({
        display: "flex",
        gap: space[4],
        font: text.body.bold.medium,
    }),
    configSection: css({
        display: "flex",
        gap: space[4],
        flexWrap: "wrap",
    }),
    environmentConfig: css({
        ...resetStyles.paragraph,
        display: "flex",
        gap: space[4],
        alignItems: "center",
        color: themeTokens.color.text.secondary,
        whiteSpace: "nowrap",
    }),
    environmentIconWrapper: css({
        alignSelf: "flex-start",
        marginTop: space[2],
    }),
    environmentDescription: css({
        wordBreak: "break-word",
    }),
    channelConfig: css({
        ...resetStyles.paragraph,
        display: "flex",
        gap: space[4],
        alignItems: "center",
        color: themeTokens.color.text.secondary,
        whiteSpace: "nowrap",
    }),
    channelIconWrapper: css({
        alignSelf: "flex-start",
        marginTop: space[2],
    }),
    channelDescription: css({
        wordBreak: "break-word",
    }),
    stepNote: css({
        padding: `0 ${space[16]}`,
        backgroundColor: `var(${stepNoteBackgroundCssVariable}, ${themeTokens.color.processStep.background.secondary.default})`,
        borderRadius: borderRadius.small,
        transitionProperty: "background-color",
        transitionDuration: "0.1s",
    }),
    disabled: css({
        opacity: 0.5,
    }),
    nameDisabled: css({
        textDecoration: "line-through",
    }),
    rootStep: css({
        boxShadow: themeTokens.shadow.extraSmall,
    }),
};
